import React, { useState, useEffect } from 'react';
import { ArrowRight, Mail, User, Briefcase, FileText, Loader2 } from 'lucide-react';
import { toast } from '@/components/ui/use-toast';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useLocation } from 'react-router-dom';

const FinalCTA = () => {
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    company: '',
    test: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const location = useLocation();

  // Check if we need to scroll to this component after navigation
  useEffect(() => {
    if (location.state && location.state.scrollToSection === 'waitlist') {
      const waitlistElement = document.getElementById('waitlist');
      if (waitlistElement) {
        setTimeout(() => {
          waitlistElement.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    }
  }, [location]);

  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    
    return emailRegex.test(email) && !email.includes('+');
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.email.trim()) {
      toast({
        variant: "destructive",
        title: "Please enter your email",
        description: "We need your email to contact you for a demo."
      });
      return;
    }

    if (!isValidEmail(formData.email)) {
      toast({
        variant: "destructive",
        title: "Invalid email format",
        description: "Please enter a valid email address."
      });
      return;
    }
    
    setIsSubmitting(true);

    try {
      const payload = {
        pipeline_id: 5,
        form: 'sign_up',
        email: formData.email,
        name: formData.name || undefined,
        details: {
          ...(formData.company ? { company: formData.company } : {}),
          ...(formData.test ? { message: formData.test } : {}),
        },
      };

      const response = await fetch('https://oursky-crm.fly.dev/api/inquiries/ingest', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': 'C6KsxpeXn9v7R9cf',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      setIsSubscribed(true);
      setFormData({
        email: '',
        name: '',
        company: '',
        test: ''
      });

      toast({
        title: "Success!",
        description: "We'll be in touch soon.",
      });
    } catch (error) {
      console.error("Error submitting to waitlist:", error);
      toast({
        variant: "destructive",
        title: "Something went wrong",
        description: "We couldn't process your information. Please try again later.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  
  return (
    <section id="waitlist" className="section-padding relative">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/3 right-1/3 w-[40%] h-[40%] rounded-full bg-brand/5 blur-[150px] animate-pulse-subtle" />
      </div>
      
      <div className="container max-w-6xl mx-auto">
        <div className="mx-auto glass-morphism rounded-3xl p-8 md:p-12 border border-dark-border relative overflow-hidden">
          <div className="absolute -top-20 -right-20 w-60 h-60 rounded-full bg-brand/10 blur-[80px]" />
          <div className="absolute -bottom-20 -left-20 w-60 h-60 rounded-full bg-brand/5 blur-[100px]" />
          
          <div className="text-center mx-auto relative z-10">
            <h2 className="text-3xl md:text-4xl lg:text-4xl font-bold mb-6 animate-fade-up tracking-tight px-4">
              <span className="text-gradient">Ready to test your real business workflows?</span>
            </h2>
            <p className="text-xl text-gray-300 mb-10 animate-fade-up" style={{ animationDelay: '0.1s' }}>
              Get early access to SkyTest, the plain-English test platform for multi-user scenarios.
            </p>
            
            {!isSubscribed ? (
              <form onSubmit={handleSubmit} className="space-y-4 max-w-md mx-auto animate-fade-up" style={{ animationDelay: '0.2s' }}>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <Input 
                    type="text" 
                    name="name"
                    placeholder="Your name" 
                    value={formData.name}
                    onChange={handleChange}
                    className="pl-12 pr-4 py-3 bg-dark-bg border border-dark-border rounded-xl focus:border-brand/50 focus:ring-2 focus:ring-brand/20 outline-none transition-all duration-300 text-white placeholder-gray-400"
                  />
                </div>
                
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <Input 
                    type="email" 
                    name="email"
                    placeholder="Your email" 
                    value={formData.email}
                    onChange={handleChange}
                    className="pl-12 pr-4 py-3 bg-dark-bg border border-dark-border rounded-xl focus:border-brand/50 focus:ring-2 focus:ring-brand/20 outline-none transition-all duration-300 text-white placeholder-gray-400"
                    required
                  />
                </div>
                
                <div className="relative">
                  <Briefcase className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <Input 
                    type="text" 
                    name="company"
                    placeholder="Your company" 
                    value={formData.company}
                    onChange={handleChange}
                    className="pl-12 pr-4 py-3 bg-dark-bg border border-dark-border rounded-xl focus:border-brand/50 focus:ring-2 focus:ring-brand/20 outline-none transition-all duration-300 text-white placeholder-gray-400"
                  />
                </div>
                
                <div className="relative flex items-start">
                  <FileText className="absolute left-4 top-4 text-gray-400 w-5 h-5" />
                  <Textarea 
                    name="test"
                    placeholder="Anything you want to share?"
                    value={formData.test}
                    onChange={handleChange}
                    className="pl-12 pr-4 py-3 min-h-[100px] bg-dark-bg border border-dark-border rounded-xl focus:border-brand/50 focus:ring-2 focus:ring-brand/20 outline-none transition-all duration-300 text-white placeholder-gray-400"
                  />
                </div>
                
                <button 
                  type="submit" 
                  className="w-full py-3 px-6 rounded-xl bg-brand hover:bg-brand-light text-white font-medium transition-default focus-ring flex items-center justify-center disabled:opacity-70 disabled:cursor-not-allowed"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      Get Early Access
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </>
                  )}
                </button>
              </form>
            ) : (
              <div className="bg-dark-bg/50 border border-dark-border rounded-xl p-4 max-w-md mx-auto animate-fade-in">
                <p className="text-[#1EAEDB] font-medium">
                  Thank you for your interest! We'll be in touch soon.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default FinalCTA;
